import type { ComponentType } from 'react'
import { INTEGRATION_REGISTRY, getIntegrationBySlug, getPrimaryIntegrationByType, type IntegrationType } from '@deepguide-ai/types'
import { getVendorLogo } from '@/lib/vendor-logo'
import {
  AlertCircle,
  BookIcon,
  CheckCircle,
  CheckIcon,
  Cloud,
  FileIcon,
  GithubIcon,
  GitlabIcon,
  RefreshCwIcon,
  Trash2,
} from 'lucide-react'

// Create icon wrapper components to satisfy the type system
const GitHubIconWrapper = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    className='text-foreground dark:text-white'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M12 1C5.9225 1 1 5.81772 1 11.7658C0.998754 14.0259 1.7247 16.2289 3.07484 18.0624C4.42498 19.8959 6.33076 21.2668 8.5218 21.9805C9.0718 22.0741 9.2775 21.7512 9.2775 21.468C9.2775 21.2129 9.2632 20.3656 9.2632 19.4634C6.5 19.9619 5.785 18.8046 5.565 18.1984C5.4407 17.8884 4.905 16.9335 4.4375 16.6772C4.0525 16.4759 3.5025 15.9774 4.4232 15.9645C5.29 15.9505 5.9082 16.7451 6.115 17.068C7.105 18.6958 8.6868 18.2383 9.3182 17.9562C9.415 17.2564 9.7032 16.786 10.02 16.5168C7.5725 16.2477 5.015 15.3186 5.015 11.2006C5.015 10.0293 5.4407 9.06147 6.1425 8.30678C6.0325 8.03763 5.6475 6.93413 6.2525 5.45383C6.2525 5.45383 7.1732 5.17177 9.2775 6.55841C10.173 6.31516 11.0982 6.19274 12.0275 6.19452C12.9625 6.19452 13.8975 6.3151 14.7775 6.55733C16.8807 5.15777 17.8025 5.45491 17.8025 5.45491C18.4075 6.93521 18.0225 8.03871 17.9125 8.30786C18.6132 9.06147 19.04 10.0164 19.04 11.2006C19.04 15.3326 16.4693 16.2477 14.0218 16.5168C14.42 16.8527 14.7643 17.4987 14.7643 18.5085C14.7643 19.9479 14.75 21.1052 14.75 21.4691C14.75 21.7512 14.9568 22.0871 15.5068 21.9794C17.6903 21.2579 19.5877 19.8844 20.9319 18.0522C22.2761 16.22 22.9994 14.0214 23 11.7658C23 5.81772 18.0775 1 12 1Z'
      fill='currentColor'
    />
  </svg>
)
const GitLabIconWrapper = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M22.643 9.37768L22.6148 9.29308L19.6199 1.49361C19.5634 1.33848 19.4504 1.21158 19.3091 1.1269C19.1679 1.0423 19.0125 1 18.843 1C18.6734 1 18.5181 1.07053 18.391 1.16928C18.2582 1.27179 18.1636 1.41581 18.1225 1.57829L16.1024 7.7558H7.92318L5.90313 1.57821C5.86076 1.42308 5.76185 1.28211 5.63475 1.16928C5.50747 1.07053 5.35219 1 5.18263 1C5.01308 1 4.8577 1.02823 4.71642 1.12698C4.57514 1.21158 4.46205 1.33848 4.40558 1.49361L1.41092 9.29308L1.38256 9.37768C0.492681 11.6908 1.25546 14.3 3.23323 15.795H3.26141C3.26141 15.8091 7.82435 19.2222 7.82435 19.2222L10.0845 20.9288L11.4549 21.9584C11.7797 22.2123 12.2459 22.2123 12.5708 21.9584L13.9411 20.9288L16.2013 19.2222L20.7924 15.795C22.7702 14.2999 23.5188 11.6908 22.6289 9.37768H22.643Z'
      fill='#E24329'
    />
    <path
      d='M22.643 9.37769L22.6148 9.29309C21.1315 9.60334 19.7612 10.224 18.5887 11.0983C18.5746 11.0983 15.0288 13.7923 12.0057 16.063C13.3978 17.1184 14.7916 18.1714 16.1872 19.2222L20.7783 15.795C22.756 14.2999 23.5048 11.6908 22.6148 9.37769H22.643Z'
      fill='#FC6D26'
    />
    <path
      d='M7.81018 19.2221L10.0704 20.9287L11.4407 21.9583C11.7655 22.2123 12.2318 22.2123 12.5567 21.9583L13.9269 20.9287L16.1872 19.2221C16.1872 19.2221 14.2378 17.7554 12.0057 16.063C9.75967 17.7554 7.81018 19.2221 7.81018 19.2221Z'
      fill='#FCA326'
    />
    <path
      d='M5.4228 11.0983C4.25027 10.2238 2.88 9.58919 1.39672 9.29309L1.36844 9.3776C0.478475 11.6907 1.24134 14.2999 3.21902 15.7949H3.24738C3.24738 15.809 7.81014 19.2221 7.81014 19.2221C7.81014 19.2221 9.75963 17.7554 12.0057 16.063C8.99676 13.7922 5.45107 11.1124 5.4228 11.0983Z'
      fill='#FC6D26'
    />
  </svg>
)
const JiraIconWrapper = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M22.081 1H11.4869C11.4869 1.62803 11.6105 2.24992 11.8509 2.83015C12.0912 3.41037 12.4435 3.93758 12.8875 4.38167C13.3316 4.82575 13.8588 5.17802 14.4391 5.41834C15.0193 5.65867 15.6412 5.78235 16.2692 5.78233H18.2208V7.66659C18.2225 10.3054 20.3612 12.4442 23 12.4459V1.91904C23 1.41161 22.5886 1 22.081 1Z'
      fill='#2684FF'
    />
    <path
      d='M16.839 6.27856H6.245C6.24663 8.91737 8.38533 11.0562 11.0242 11.0579H12.9758V12.9482C12.9791 15.587 15.1193 17.7243 17.7581 17.7243V7.19777C17.7581 6.69017 17.3466 6.27856 16.839 6.27856Z'
      fill='url(#paint0_linear_1906_396)'
    />
    <path
      d='M11.5941 11.5543H1C1 14.1955 3.1412 16.3367 5.78233 16.3367H7.74V18.2208C7.74172 20.8572 9.87672 22.9951 12.5132 23.0001V12.4734C12.5132 11.9658 12.1017 11.5543 11.5941 11.5543Z'
      fill='url(#paint1_linear_1906_396)'
    />
    <defs>
      <linearGradient
        id='paint0_linear_1906_396'
        x1='1134.89'
        y1='8.12134'
        x2='686.971'
        y2='475.503'
        gradientUnits='userSpaceOnUse'
      >
        <stop offset='0.18' stopColor='#0052CC' />
        <stop offset='1' stopColor='#2684FF' />
      </linearGradient>
      <linearGradient
        id='paint1_linear_1906_396'
        x1='1159.98'
        y1='16.7621'
        x2='641.849'
        y2='526.532'
        gradientUnits='userSpaceOnUse'
      >
        <stop offset='0.18' stopColor='#0052CC' />
        <stop offset='1' stopColor='#2684FF' />
      </linearGradient>
    </defs>
  </svg>
)

const LinearIconWrapper = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M1.04271 13.1844C1.30255 15.6872 2.41763 18.0238 4.19986 19.8001C5.97584 21.5821 8.31198 22.6972 10.8144 22.9573L1.04271 13.1844ZM1 11.4161L12.5839 23C13.5721 22.9449 14.5483 22.7565 15.486 22.4398L1.56138 8.51277C1.24424 9.45081 1.0554 10.4274 1 11.4161ZM2.05808 7.28628L16.7137 21.9419C17.4859 21.571 18.2121 21.1111 18.8775 20.5714L3.42857 5.12132C2.88884 5.78703 2.42888 6.51363 2.05808 7.28628ZM4.24989 4.21701C6.30971 2.15719 9.10342 1 12.0164 1C14.9295 1 17.7232 2.15719 19.783 4.21701C21.8428 6.27683 23 9.07054 23 11.9836C23 14.8966 21.8428 17.6903 19.783 19.7501L4.24989 4.21701Z'
      fill='#829AFF'
    />
  </svg>
)
const IntercomIconWrapper = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M20.25 1H3.75C3.02067 1.00003 2.32121 1.28978 1.80549 1.80549C1.28978 2.32121 1.00003 3.02067 1 3.75V20.25C0.999901 20.6112 1.07096 20.9688 1.20913 21.3025C1.3473 21.6362 1.54986 21.9394 1.80524 22.1948C2.06062 22.4501 2.36381 22.6527 2.6975 22.7909C3.03119 22.929 3.38884 23.0001 3.75 23H20.25C20.6112 23.0002 20.9689 22.9292 21.3026 22.7911C21.6364 22.653 21.9396 22.4504 22.195 22.195C22.4504 21.9396 22.653 21.6364 22.7911 21.3026C22.9292 20.9689 23.0002 20.6112 23 20.25V3.75C23.0001 3.38884 22.929 3.03119 22.7909 2.6975C22.6527 2.36381 22.4501 2.06062 22.1948 1.80524C21.9394 1.54986 21.6362 1.3473 21.3025 1.20913C20.9688 1.07096 20.6112 0.999901 20.25 1ZM14.9322 5.03219C14.9322 4.83745 15.0095 4.65069 15.1472 4.51299C15.2849 4.3753 15.4717 4.29794 15.6664 4.29794C15.8612 4.29794 16.0479 4.3753 16.1856 4.51299C16.3233 4.65069 16.4007 4.83745 16.4007 5.03219V14.8297C16.4007 15.0245 16.3233 15.2112 16.1856 15.3489C16.0479 15.4866 15.8612 15.564 15.6664 15.564C15.4717 15.564 15.2849 15.4866 15.1472 15.3489C15.0095 15.2112 14.9322 15.0245 14.9322 14.8297V5.03219ZM11.2664 4.66094C11.2664 4.46638 11.3437 4.2798 11.4813 4.14223C11.6189 4.00466 11.8054 3.92737 12 3.92737C12.1946 3.92737 12.3811 4.00466 12.5187 4.14223C12.6563 4.2798 12.7336 4.46638 12.7336 4.66094V15.2959C12.7336 15.4904 12.6563 15.677 12.5187 15.8146C12.3811 15.9522 12.1946 16.0294 12 16.0294C11.8054 16.0294 11.6189 15.9522 11.4813 15.8146C11.3437 15.677 11.2664 15.4904 11.2664 15.2959V4.66094ZM7.6 5.03219C7.6 4.83763 7.67729 4.65105 7.81486 4.51348C7.95242 4.37591 8.13901 4.29862 8.33356 4.29862C8.52812 4.29862 8.7147 4.37591 8.85227 4.51348C8.98984 4.65105 9.06712 4.83763 9.06712 5.03219V14.8297C9.06712 15.0243 8.98984 15.2109 8.85227 15.3485C8.7147 15.486 8.52812 15.5633 8.33356 15.5633C8.13901 15.5633 7.95242 15.486 7.81486 15.3485C7.67729 15.2109 7.6 15.0243 7.6 14.8297V5.03219ZM3.93219 6.5C3.93219 6.40367 3.95116 6.30828 3.98803 6.21928C4.02489 6.13028 4.07893 6.04941 4.14704 5.98129C4.21516 5.91318 4.29603 5.85914 4.38503 5.82228C4.47403 5.78541 4.56942 5.76644 4.66575 5.76644C4.76208 5.76644 4.85747 5.78541 4.94647 5.82228C5.03547 5.85914 5.11634 5.91318 5.18446 5.98129C5.25257 6.04941 5.30661 6.13028 5.34347 6.21928C5.38034 6.30828 5.39931 6.40367 5.39931 6.5V13.0952C5.39931 13.2897 5.32203 13.4763 5.18446 13.6139C5.04689 13.7515 4.8603 13.8287 4.66575 13.8287C4.4712 13.8287 4.28461 13.7515 4.14704 13.6139C4.00947 13.4763 3.93219 13.2897 3.93219 13.0952V6.5ZM19.81 17.6849C19.6972 17.7812 16.9809 20.0616 12 20.0616C7.01975 20.0616 4.30275 17.7826 4.18931 17.6849C4.04126 17.5587 3.94942 17.3788 3.93401 17.1848C3.91861 16.9908 3.98089 16.7987 4.10716 16.6506C4.23342 16.5025 4.41334 16.4107 4.60731 16.3953C4.80129 16.3799 4.99344 16.4422 5.1415 16.5684C5.18481 16.6062 7.61031 18.5952 11.9993 18.5952C16.4433 18.5952 18.8337 16.5911 18.855 16.5719C18.928 16.509 19.0128 16.4612 19.1043 16.4311C19.1959 16.4011 19.2925 16.3893 19.3886 16.3966C19.4847 16.4039 19.5784 16.4301 19.6644 16.4736C19.7504 16.5171 19.8269 16.5772 19.8897 16.6503C19.9524 16.7235 20.0001 16.8083 20.0301 16.8999C20.0601 16.9915 20.0717 17.0881 20.0643 17.1842C20.0568 17.2803 20.0306 17.3739 19.9869 17.4599C19.9433 17.5458 19.8831 17.6223 19.8099 17.6849H19.81ZM20.0678 13.0952C20.0678 13.2899 19.9905 13.4767 19.8528 13.6144C19.7151 13.7521 19.5283 13.8294 19.3336 13.8294C19.1388 13.8294 18.9521 13.7521 18.8144 13.6144C18.6767 13.4767 18.5993 13.2899 18.5993 13.0952V6.5C18.5993 6.30526 18.6767 6.11851 18.8144 5.98081C18.9521 5.84311 19.1388 5.76575 19.3336 5.76575C19.5283 5.76575 19.7151 5.84311 19.8528 5.98081C19.9905 6.11851 20.0678 6.30526 20.0678 6.5V13.0952Z'
      fill='#1F8DED'
    />
  </svg>
)
const CheckCircleIconWrapper = () => <CheckCircle className='h-5 w-5' />
const AlertCircleIconWrapper = () => <AlertCircle className='h-5 w-5' />
const CheckIconWrapper = () => <CheckIcon className='h-5 w-5' />
const RefreshCwIconWrapper = () => <RefreshCwIcon className='h-5 w-5' />
const Trash2IconWrapper = () => <Trash2 className='mr-2 h-4 w-4' />
const DocusaurusIconWrapper = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M21.3077 14.5589C21.9308 14.5589 22.4359 14.0538 22.4359 13.4307C22.4359 12.8076 21.9308 12.3025 21.3077 12.3025C20.6846 12.3025 20.1795 12.8076 20.1795 13.4307C20.1795 14.0538 20.6846 14.5589 21.3077 14.5589Z'
      fill='#44D860'
    />
    <path
      d='M21.8718 12.72C22.0213 12.72 22.1426 12.5988 22.1426 12.4492C22.1426 12.2997 22.0213 12.1785 21.8718 12.1785C21.7222 12.1785 21.601 12.2997 21.601 12.4492C21.601 12.5988 21.7222 12.72 21.8718 12.72Z'
      fill='#44D860'
    />
    <path
      d='M22.2893 13.1374C22.4388 13.1374 22.56 13.0161 22.56 12.8666C22.56 12.7171 22.4388 12.5958 22.2893 12.5958C22.1397 12.5958 22.0185 12.7171 22.0185 12.8666C22.0185 13.0161 22.1397 13.1374 22.2893 13.1374Z'
      fill='#44D860'
    />
    <path
      d='M22.4359 13.7015C22.5855 13.7015 22.7067 13.5802 22.7067 13.4307C22.7067 13.2811 22.5855 13.1599 22.4359 13.1599C22.2864 13.1599 22.1652 13.2811 22.1652 13.4307C22.1652 13.5802 22.2864 13.7015 22.4359 13.7015Z'
      fill='#44D860'
    />
    <path
      d='M22.2893 14.2657C22.4388 14.2657 22.56 14.1444 22.56 13.9949C22.56 13.8453 22.4388 13.7241 22.2893 13.7241C22.1397 13.7241 22.0185 13.8453 22.0185 13.9949C22.0185 14.1444 22.1397 14.2657 22.2893 14.2657Z'
      fill='#44D860'
    />
    <path
      d='M21.8718 14.683C22.0213 14.683 22.1426 14.5618 22.1426 14.4122C22.1426 14.2627 22.0213 14.1415 21.8718 14.1415C21.7222 14.1415 21.601 14.2627 21.601 14.4122C21.601 14.5618 21.7222 14.683 21.8718 14.683Z'
      fill='#44D860'
    />
    <path
      d='M20.1795 21.3281C21.4257 21.3281 22.4359 20.3179 22.4359 19.0717C22.4359 17.8255 21.4257 16.8153 20.1795 16.8153C18.9333 16.8153 17.9231 17.8255 17.9231 19.0717C17.9231 20.3179 18.9333 21.3281 20.1795 21.3281Z'
      fill='#44D860'
    />
    <path
      d='M21.3077 17.6841C21.6192 17.6841 21.8718 17.4316 21.8718 17.12C21.8718 16.8085 21.6192 16.5559 21.3077 16.5559C20.9961 16.5559 20.7436 16.8085 20.7436 17.12C20.7436 17.4316 20.9961 17.6841 21.3077 17.6841Z'
      fill='#44D860'
    />
    <path
      d='M22.1312 18.5076C22.4428 18.5076 22.6953 18.255 22.6953 17.9435C22.6953 17.632 22.4428 17.3794 22.1312 17.3794C21.8197 17.3794 21.5671 17.632 21.5671 17.9435C21.5671 18.255 21.8197 18.5076 22.1312 18.5076Z'
      fill='#44D860'
    />
    <path
      d='M22.4359 19.6359C22.7475 19.6359 23 19.3833 23 19.0718C23 18.7602 22.7475 18.5077 22.4359 18.5077C22.1244 18.5077 21.8718 18.7602 21.8718 19.0718C21.8718 19.3833 22.1244 19.6359 22.4359 19.6359Z'
      fill='#44D860'
    />
    <path d='M11.8308 6.54871H21.3077V10.3846H11.8308V6.54871Z' fill='white' />
    <path
      d='M3.25641 19.0717C2.42154 19.0717 1.67692 18.6204 1.30462 17.9435C1.10577 18.2863 1.0007 18.6754 1 19.0717C1 20.3127 2.01538 21.3281 3.25641 21.3281H5.51282V19.0717H3.25641ZM16.7949 21.3281H20.1795V16.8153H16.7949V21.3281Z'
      fill='#3ECC5F'
    />
    <path
      d='M13.4102 7.15792L21.3077 6.66151V5.53331C21.3077 4.29228 20.2923 3.2769 19.0513 3.2769H8.89742C8.44613 2.37433 8.22049 2.37433 7.76921 3.2769C7.31793 2.37433 7.09229 2.37433 6.64101 3.2769C6.18972 2.37433 5.96408 2.37433 5.5128 3.2769C4.83588 2.26151 4.49742 2.7128 4.35075 3.53639C3.44819 3.19792 3.10972 3.19792 3.52716 4.35998C2.6246 4.58562 2.39895 4.81126 3.25639 5.53331C2.35383 5.98459 2.35383 6.21023 3.25639 6.66151C2.35383 7.1128 2.35383 7.33844 3.25639 7.78972C2.35383 8.241 2.35383 8.46664 3.25639 8.91792C2.35383 9.36921 2.35383 9.59485 3.25639 10.0461C2.35383 10.4974 2.35383 10.7231 3.25639 11.1743C2.35383 11.6256 2.35383 11.8513 3.25639 12.3025C2.35383 12.7538 2.35383 12.9795 3.25639 13.4307C2.35383 13.882 2.35383 14.1077 3.25639 14.559C2.35383 15.0102 2.35383 15.2359 3.25639 15.6872C2.35383 16.1384 2.35383 16.3641 3.25639 16.8154C2.35383 17.2666 2.35383 17.4923 3.25639 17.9436C2.35383 18.3948 2.35383 18.6205 3.25639 19.0718C3.25639 20.3128 4.27178 21.3282 5.5128 21.3282H19.0513C20.2923 21.3282 21.3077 20.3128 21.3077 19.0718V10.0461L13.4102 9.561C13.103 9.5436 12.8143 9.40842 12.6041 9.18358C12.394 8.95874 12.2786 8.66155 12.282 8.35382C12.282 7.72203 12.7784 7.19177 13.4102 7.15792Z'
      fill='#3ECC5F'
    />
    <path
      d='M12.282 21.3282H19.0513C20.2923 21.3282 21.3077 20.3128 21.3077 19.0718V11.1743H14.5385C13.2974 11.1743 12.282 12.1897 12.282 13.4307V21.3282Z'
      fill='#FFFF50'
    />
    <path
      d='M7.76923 6.66153C7.76923 6.36231 7.65036 6.07535 7.43878 5.86377C7.2272 5.65219 6.94024 5.53333 6.64102 5.53333C6.3418 5.53333 6.05484 5.65219 5.84326 5.86377C5.63168 6.07535 5.51282 6.36231 5.51282 6.66153'
      stroke='black'
      stroke-width='0.3'
      stroke-linecap='round'
    />
    <path
      d='M21.3077 7.74463C20.7436 7.74463 20.7436 8.87283 20.1795 8.95181C19.6154 8.95181 19.6154 7.8236 19.0513 7.8236C18.4872 7.8236 18.4872 8.83899 17.9231 8.83899C17.359 8.83899 17.359 7.88001 16.7949 7.88001C16.2308 7.88001 16.2308 8.78258 15.6667 8.78258C15.1026 8.78258 15.1026 7.96463 14.5385 7.96463C13.9744 7.96463 13.9744 8.69796 13.4103 8.69796'
      stroke='black'
      stroke-width='0.12'
    />
    <path
      d='M19.6154 13.4308H13.9744M19.6154 14.559H13.9744M19.6154 15.6872H13.9744M19.6154 16.8154H13.9744M19.6154 17.9436H13.9744M19.6154 19.0718H13.9744'
      stroke='black'
      stroke-width='0.12'
      stroke-linecap='round'
    />
    <path
      d='M16.7949 5.39784C16.9506 5.39784 17.0769 5.27156 17.0769 5.11579C17.0769 4.96002 16.9506 4.83374 16.7949 4.83374C16.6391 4.83374 16.5128 4.96002 16.5128 5.11579C16.5128 5.27156 16.6391 5.39784 16.7949 5.39784Z'
      fill='black'
    />
    <path
      d='M19.0513 5.25124C19.2071 5.25124 19.3333 5.12496 19.3333 4.96919C19.3333 4.81341 19.2071 4.68713 19.0513 4.68713C18.8955 4.68713 18.7692 4.81341 18.7692 4.96919C18.7692 5.12496 18.8955 5.25124 19.0513 5.25124Z'
      fill='black'
    />
    <path
      d='M13.4102 8.80511C13.4726 8.80511 13.5231 8.7546 13.5231 8.69229C13.5231 8.62998 13.4726 8.57947 13.4102 8.57947C13.3479 8.57947 13.2974 8.62998 13.2974 8.69229C13.2974 8.7546 13.3479 8.80511 13.4102 8.80511Z'
      fill='black'
    />
    <path
      d='M10.0257 14.5589H14.5385V12.3025H10.0257V14.5589ZM10.0257 21.3281H14.5385V16.8153H10.0257V21.3281Z'
      fill='#3ECC5F'
    />
    <path
      d='M14.5385 14.5589C15.1616 14.5589 15.6667 14.0538 15.6667 13.4307C15.6667 12.8076 15.1616 12.3025 14.5385 12.3025C13.9154 12.3025 13.4103 12.8076 13.4103 13.4307C13.4103 14.0538 13.9154 14.5589 14.5385 14.5589Z'
      fill='#44D860'
    />
    <path
      d='M15.1026 12.72C15.2521 12.72 15.3733 12.5988 15.3733 12.4492C15.3733 12.2997 15.2521 12.1785 15.1026 12.1785C14.953 12.1785 14.8318 12.2997 14.8318 12.4492C14.8318 12.5988 14.953 12.72 15.1026 12.72Z'
      fill='#44D860'
    />
    <path
      d='M15.52 13.1374C15.6696 13.1374 15.7908 13.0161 15.7908 12.8666C15.7908 12.7171 15.6696 12.5958 15.52 12.5958C15.3705 12.5958 15.2493 12.7171 15.2493 12.8666C15.2493 13.0161 15.3705 13.1374 15.52 13.1374Z'
      fill='#44D860'
    />
    <path
      d='M15.6667 13.7015C15.8162 13.7015 15.9375 13.5802 15.9375 13.4307C15.9375 13.2811 15.8162 13.1599 15.6667 13.1599C15.5172 13.1599 15.3959 13.2811 15.3959 13.4307C15.3959 13.5802 15.5172 13.7015 15.6667 13.7015Z'
      fill='#44D860'
    />
    <path
      d='M15.52 14.2657C15.6696 14.2657 15.7908 14.1444 15.7908 13.9949C15.7908 13.8453 15.6696 13.7241 15.52 13.7241C15.3705 13.7241 15.2493 13.8453 15.2493 13.9949C15.2493 14.1444 15.3705 14.2657 15.52 14.2657Z'
      fill='#44D860'
    />
    <path
      d='M15.1026 14.683C15.2521 14.683 15.3733 14.5618 15.3733 14.4122C15.3733 14.2627 15.2521 14.1415 15.1026 14.1415C14.953 14.1415 14.8318 14.2627 14.8318 14.4122C14.8318 14.5618 14.953 14.683 15.1026 14.683Z'
      fill='#44D860'
    />
    <path
      d='M14.5385 21.3281C15.7846 21.3281 16.7949 20.3179 16.7949 19.0717C16.7949 17.8255 15.7846 16.8153 14.5385 16.8153C13.2923 16.8153 12.282 17.8255 12.282 19.0717C12.282 20.3179 13.2923 21.3281 14.5385 21.3281Z'
      fill='#44D860'
    />
    <path
      d='M13.4102 17.6841C13.7218 17.6841 13.9743 17.4316 13.9743 17.12C13.9743 16.8085 13.7218 16.5559 13.4102 16.5559C13.0987 16.5559 12.8461 16.8085 12.8461 17.12C12.8461 17.4316 13.0987 17.6841 13.4102 17.6841Z'
      fill='#44D860'
    />
    <path
      d='M14.5385 17.3794C14.85 17.3794 15.1026 17.1269 15.1026 16.8153C15.1026 16.5038 14.85 16.2512 14.5385 16.2512C14.2269 16.2512 13.9744 16.5038 13.9744 16.8153C13.9744 17.1269 14.2269 17.3794 14.5385 17.3794Z'
      fill='#44D860'
    />
    <path
      d='M15.6666 17.6841C15.9782 17.6841 16.2307 17.4316 16.2307 17.12C16.2307 16.8085 15.9782 16.5559 15.6666 16.5559C15.3551 16.5559 15.1025 16.8085 15.1025 17.12C15.1025 17.4316 15.3551 17.6841 15.6666 17.6841Z'
      fill='#44D860'
    />
    <path
      d='M16.4903 18.5076C16.8018 18.5076 17.0544 18.255 17.0544 17.9435C17.0544 17.632 16.8018 17.3794 16.4903 17.3794C16.1787 17.3794 15.9261 17.632 15.9261 17.9435C15.9261 18.255 16.1787 18.5076 16.4903 18.5076Z'
      fill='#44D860'
    />
    <path
      d='M16.7949 19.6359C17.1064 19.6359 17.359 19.3833 17.359 19.0718C17.359 18.7602 17.1064 18.5077 16.7949 18.5077C16.4833 18.5077 16.2308 18.7602 16.2308 19.0718C16.2308 19.3833 16.4833 19.6359 16.7949 19.6359Z'
      fill='#44D860'
    />
  </svg>
)

const FileIconWrapper = () => <FileIcon className='h-5 w-5' />
const VitePressIconWrapper = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <g clip-path='url(#clip0_1906_510)'>
      <path
        d='M2.51814 3.93909C2.37668 2.91978 3.07796 1.97733 4.0845 1.83407L16.8419 0.0183701C17.8484 -0.124885 18.779 0.5853 18.9205 1.60461L21.4818 20.0609C21.6233 21.0802 20.9221 22.0227 19.9155 22.166L7.1581 23.9816C6.15155 24.1249 5.22095 23.4147 5.0795 22.3954L2.51814 3.93909Z'
        fill='url(#paint0_linear_1906_510)'
      />
      <path
        d='M3.42936 3.80941C3.35863 3.29975 3.70927 2.82852 4.21253 2.7569L16.9699 0.941192C17.4732 0.869562 17.9385 1.22466 18.0093 1.73431L20.5706 20.1906C20.6413 20.7003 20.2907 21.1715 19.7874 21.2431L7.03002 23.0588C6.52672 23.1305 6.06142 22.7754 5.99067 22.2657L3.42936 3.80941Z'
        fill='white'
      />
      <path
        d='M16.5928 7.4598L12.9252 17.0788C12.8495 17.2774 12.5881 17.315 12.4588 17.146L6.16712 8.917C6.02627 8.7328 6.16082 8.467 6.39027 8.47625L11.4526 8.6804C11.4849 8.6817 11.5172 8.6771 11.5478 8.6669L16.2544 7.09965C16.4715 7.02735 16.6751 7.24395 16.5928 7.4598Z'
        fill='url(#paint1_linear_1906_510)'
      />
      <path
        d='M13.5125 6.28785L9.9676 7.5214C9.90935 7.54165 9.8722 7.59935 9.8773 7.6616L10.1958 11.5315C10.2033 11.6227 10.2952 11.6814 10.3794 11.6489L11.3613 11.2696C11.4532 11.2341 11.551 11.3069 11.5452 11.4064L11.4551 12.9452C11.4491 13.0487 11.5546 13.1218 11.6475 13.0784L12.2474 12.7977C12.3404 12.7542 12.446 12.8275 12.4397 12.9312L12.2927 15.349C12.2835 15.5002 12.4879 15.5534 12.5506 15.4161L12.5925 15.3244L14.7149 8.90075C14.7504 8.7932 14.6484 8.6905 14.5423 8.72705L13.5259 9.0774C13.4304 9.1103 13.3337 9.02935 13.3477 8.9281L13.6911 6.437C13.7051 6.33565 13.6081 6.2546 13.5125 6.28785Z'
        fill='url(#paint2_linear_1906_510)'
      />
    </g>
    <defs>
      <linearGradient
        id='paint0_linear_1906_510'
        x1='3.24082'
        y1='0.98795'
        x2='19.525'
        y2='24.1032'
        gradientUnits='userSpaceOnUse'
      >
        <stop stopColor='#49C7FF' />
        <stop offset='1' stopColor='#BD36FF' />
      </linearGradient>
      <linearGradient
        id='paint1_linear_1906_510'
        x1='5.94237'
        y1='8.21335'
        x2='13.3623'
        y2='15.7089'
        gradientUnits='userSpaceOnUse'
      >
        <stop stopColor='#41D1FF' />
        <stop offset='1' stopColor='#BD34FE' />
      </linearGradient>
      <linearGradient
        id='paint2_linear_1906_510'
        x1='10.9069'
        y1='6.85235'
        x2='13.1232'
        y2='14.4035'
        gradientUnits='userSpaceOnUse'
      >
        <stop stopColor='#FFEA83' />
        <stop offset='0.0833333' stopColor='#FFDD35' />
        <stop offset='1' stopColor='#FFA800' />
      </linearGradient>
      <clipPath id='clip0_1906_510'>
        <rect width='24' height='24' fill='white' />
      </clipPath>
    </defs>
  </svg>
)

const GitBookIconWrapper = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M12.5437 1.5C11.9229 1.5 11.357 1.82745 10.227 2.47945L4.03717 6.04864C2.97113 6.65093 2.08275 7.52397 1.46171 8.57963C0.840659 9.63529 0.508929 10.8362 0.5 12.0612L0.5 12.2884C0.508998 13.5118 0.839966 14.7112 1.45958 15.7659C2.07919 16.8206 2.96557 17.6933 4.02947 18.2961L7.9016 20.5334C10.1673 21.8432 11.3002 22.5 12.5447 22.5C13.7882 22.501 14.922 21.847 17.1887 20.5372L21.2803 18.1748C22.4131 17.5199 22.9781 17.1925 23.289 16.6541C23.5999 16.1148 23.6009 15.4599 23.5999 14.1501V11.6172C23.6001 11.4414 23.5541 11.2688 23.4665 11.1165C23.3789 10.9641 23.2528 10.8376 23.1009 10.7495C22.9489 10.6613 22.7765 10.6148 22.6009 10.6144C22.4252 10.6141 22.2526 10.66 22.1003 10.7475L13.6997 15.586C13.1347 15.9106 12.8527 16.0743 12.5437 16.0743C12.2328 16.0743 11.9508 15.9125 11.3849 15.587L5.70229 12.3193C5.41643 12.1546 5.27302 12.0727 5.15848 12.0583C5.03192 12.0415 4.90339 12.0673 4.79299 12.1314C4.68258 12.1955 4.59652 12.2944 4.54826 12.4127C4.50495 12.5196 4.50495 12.6843 4.50687 13.0146C4.5088 13.2573 4.50687 13.3787 4.53093 13.4904C4.58195 13.7398 4.71285 13.9652 4.90342 14.1327C4.98908 14.2069 5.09399 14.2676 5.30382 14.3889L11.382 17.9042C11.9489 18.2316 12.2328 18.3953 12.5437 18.3953C12.8546 18.3953 13.1376 18.2316 13.7045 17.9051L21.1542 13.6011C21.3486 13.4894 21.4449 13.4355 21.5171 13.4759C21.5893 13.5183 21.5893 13.63 21.5893 13.8534V15.0024C21.5893 15.3299 21.5902 15.4936 21.5123 15.6274C21.4353 15.7623 21.2938 15.8432 21.0098 16.0069L14.8672 19.5559C13.7334 20.2108 13.1664 20.5382 12.5447 20.5382C11.9229 20.5382 11.355 20.2108 10.2231 19.5539L4.47703 16.2294L4.43565 16.2053C3.84347 15.859 3.35173 15.3641 3.00893 14.7697C2.66613 14.1752 2.48411 13.5016 2.48082 12.8152V11.7116C2.4811 11.3324 2.5808 10.9599 2.76996 10.6314C2.95913 10.3029 3.23114 10.0297 3.55881 9.83932C3.84867 9.67145 4.17761 9.58298 4.51252 9.58281C4.84742 9.58264 5.17646 9.67078 5.46648 9.83836L10.227 12.5908C11.357 13.2448 11.9229 13.5722 12.5437 13.5732C13.1645 13.5732 13.7305 13.2457 14.8633 12.5937L22.0647 8.44671C22.2247 8.35458 22.3575 8.22189 22.4499 8.06203C22.5422 7.90217 22.5909 7.72077 22.5909 7.53611C22.5909 7.35146 22.5422 7.17006 22.4499 7.0102C22.3575 6.85034 22.2247 6.71765 22.0647 6.62552L14.8604 2.4756C13.7295 1.82552 13.1645 1.5 12.5447 1.5H12.5437Z'
      fill='#307F98'
    />
  </svg>
)

const LocalFileIconWrapper = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M10.1846 4.16345H2.76923C2.56522 4.16345 2.36956 4.2445 2.2253 4.38875C2.08104 4.53301 2 4.72867 2 4.93268V17.2403C2 17.4444 2.08104 17.64 2.2253 17.7843C2.36956 17.9285 2.56522 18.0096 2.76923 18.0096H21.2308C21.4348 18.0096 21.6304 17.9285 21.7747 17.7843C21.919 17.64 22 17.4444 22 17.2403V12.288M10.4615 18.0096L8.92308 21.8557M13.5385 18.0096L15.0769 21.8557M7.38462 21.8557H16.6154'
      stroke='black'
      stroke-width='1.5'
      stroke-linecap='round'
      stroke-linejoin='round'
    />
    <path
      d='M21.8923 9.02335C21.8923 9.39258 21.5923 9.69258 21.2231 9.69258H13.8846C13.7074 9.69217 13.5376 9.62148 13.4124 9.49602C13.2872 9.37056 13.2169 9.20057 13.2169 9.02335V2.66952C13.2169 2.49203 13.2874 2.32181 13.4129 2.1963C13.5384 2.0708 13.7087 2.00029 13.8861 2.00029H16.3538C16.5066 1.99579 16.6563 2.0437 16.778 2.13606C16.8998 2.22842 16.9863 2.35967 17.0231 2.50798L17.2108 3.33875H21.2231C21.4003 3.33875 21.5703 3.40904 21.6957 3.53422C21.8212 3.65939 21.8919 3.82922 21.8923 4.00644V9.02335Z'
      stroke='black'
      stroke-width='1.5'
      stroke-linecap='round'
      stroke-linejoin='round'
    />
  </svg>
)

const GoogleDriveIconWrapper = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M2.66322 18.5097L3.63345 20.1519C3.83506 20.4977 4.12484 20.7694 4.46506 20.967C5.43953 19.7548 6.11786 18.8245 6.50005 18.1763C6.88791 17.5184 7.3646 16.4894 7.93012 15.0892C6.40595 14.8926 5.25095 14.7943 4.46515 14.7943C3.71091 14.7943 2.55586 14.8926 1 15.0892C1 15.472 1.1008 15.8548 1.30241 16.2006L2.66322 18.5097Z'
      fill='#0066DA'
    />
    <path
      d='M19.5349 20.967C19.8753 20.7694 20.165 20.4977 20.3666 20.152L20.7698 19.4729L22.6977 16.2006C22.8955 15.8623 22.9998 15.4792 23 15.0892C21.4351 14.8926 20.2822 14.7943 19.5413 14.7943C18.745 14.7943 17.5921 14.8926 16.0825 15.0892C16.6413 16.4971 17.1118 17.5261 17.4938 18.1763C17.879 18.8322 18.5594 19.7625 19.5349 20.967Z'
      fill='#EA4335'
    />
    <path
      d='M12 8.17412C13.1275 6.83965 13.9045 5.81065 14.331 5.0871C14.6745 4.50448 15.0525 3.57423 15.465 2.29636C15.1248 2.09879 14.7342 2 14.331 2H9.66894C9.26572 2 8.87522 2.11117 8.53491 2.29636C9.0597 3.76193 9.50497 4.80494 9.87071 5.4254C10.275 6.11111 10.9847 7.02735 12 8.17412Z'
      fill='#00832D'
    />
    <path
      d='M16.0698 15.0891H7.93006L4.46509 20.9669C4.80523 21.1644 5.19581 21.2632 5.59902 21.2632H18.4009C18.8041 21.2632 19.1948 21.1521 19.5349 20.9668L16.0698 15.0891Z'
      fill='#2684FC'
    />
    <path
      d='M12 8.17406L8.53503 2.29639C8.19472 2.49396 7.90494 2.76557 7.70333 3.11137L1.30241 13.9778C1.10452 14.3161 1.00027 14.6992 1 15.0891H7.93012L12 8.17406Z'
      fill='#00AC47'
    />
    <path
      d='M19.4973 8.54462L16.2967 3.11137C16.0952 2.76557 15.8053 2.49396 15.4651 2.29639L12.0001 8.17414L16.0699 15.0892H22.9875C22.9875 14.7064 22.8867 14.3237 22.6851 13.9779L19.4973 8.54462Z'
      fill='#FFBA00'
    />
  </svg>
)

const NotionIconWrapper = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M14.97 0.801272L2.48266 1.72076C1.47618 1.80776 1.12518 2.464 1.12518 3.25073V16.899C1.12518 17.5118 1.34418 18.036 1.86992 18.7365L4.80537 22.5419C5.28762 23.1547 5.72561 23.2859 6.6466 23.2424L21.1476 22.3672C22.3738 22.2802 22.7248 21.7109 22.7248 20.7487V5.3942C22.7248 4.89696 22.5276 4.75371 21.9471 4.32922C21.9141 4.30527 21.8811 4.28127 21.8481 4.25722L17.8619 1.45751C16.8974 0.758523 16.5037 0.670024 14.97 0.801272ZM6.97509 5.14295C5.79086 5.22245 5.52236 5.24045 4.84962 4.69521L3.13965 3.33923C2.96565 3.16373 3.0534 2.94474 3.49065 2.90124L15.4957 2.02675C16.5029 1.939 17.0287 2.28925 17.4232 2.59524L19.4826 4.08322C19.5704 4.12672 19.7886 4.38846 19.5261 4.38846L7.12809 5.13245L6.97509 5.14295ZM5.59436 20.6175V7.58266C5.59436 7.01342 5.76911 6.75093 6.29485 6.70668L20.5334 5.87569C21.0164 5.83219 21.2354 6.13819 21.2354 6.70668V19.6545C21.2354 20.2237 21.1476 20.7052 20.3579 20.7487L6.73284 21.5362C5.94461 21.5797 5.59436 21.3179 5.59436 20.6175ZM19.0454 8.28165C19.1324 8.6754 19.0454 9.06914 18.6502 9.11414L17.9932 9.24389V18.8677C17.4232 19.1737 16.8982 19.3485 16.4602 19.3485C15.7589 19.3485 15.5827 19.1295 15.0577 18.474L10.7633 11.7376V18.255L12.1223 18.5617C12.1223 18.5617 12.1223 19.3492 11.0258 19.3492L8.00332 19.524C7.91632 19.3485 8.00332 18.9112 8.31082 18.8242L9.09831 18.606V9.98862L8.00332 9.90013C7.91632 9.50638 8.13457 8.93789 8.74881 8.89439L11.991 8.6754L16.4602 15.5003V9.46288L15.3202 9.33239C15.2332 8.85014 15.5827 8.50065 16.0214 8.45715L19.0454 8.28165Z'
      fill='var(--foreground)'
    />
  </svg>
)

const ConfluenceIconWrapper = () => (
  <svg
    width='25'
    height='24'
    viewBox='0 0 25 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M2.29762 17.7672C2.07019 18.1412 1.81459 18.5753 1.59758 18.921C1.50421 19.0801 1.47647 19.2697 1.52032 19.4492C1.56416 19.6286 1.6761 19.7836 1.83208 19.8808L6.38269 22.7036C6.46177 22.7528 6.54978 22.7857 6.64159 22.8002C6.73339 22.8148 6.82716 22.8108 6.91742 22.7885C7.00768 22.7662 7.09262 22.7259 7.1673 22.6702C7.24198 22.6144 7.30489 22.5442 7.35237 22.4637C7.53441 22.1566 7.7689 21.7579 8.02442 21.331C9.82716 18.3318 11.6404 18.6987 14.9098 20.2724L19.4219 22.4354C19.5065 22.4759 19.5983 22.499 19.6919 22.5031C19.7855 22.5073 19.8789 22.4925 19.9667 22.4596C20.0545 22.4267 20.1349 22.3764 20.203 22.3116C20.2712 22.2468 20.3258 22.1689 20.3635 22.0825L22.5303 17.1427C22.6039 16.9731 22.6085 16.7812 22.543 16.6083C22.4775 16.4353 22.3473 16.2953 22.1803 16.2182C21.2282 15.7666 19.3345 14.8669 17.6297 14.0376C11.4969 11.0349 6.28465 11.229 2.29762 17.7672Z'
      fill='url(#paint0_linear_1906_433)'
    />
    <path
      d='M22.7024 6.55671C22.9299 6.1827 23.1854 5.74869 23.4025 5.4029C23.4958 5.24383 23.5236 5.05419 23.4797 4.87473C23.4358 4.69527 23.3239 4.54032 23.1679 4.44317L18.6173 1.62034C18.5376 1.56627 18.4478 1.5293 18.3533 1.51172C18.2589 1.49415 18.1618 1.49635 18.0683 1.5182C17.9747 1.54004 17.8866 1.58106 17.8094 1.6387C17.7322 1.69634 17.6677 1.76937 17.6197 1.85324C17.4377 2.16021 17.2032 2.55897 16.9476 2.98586C15.1448 5.98506 13.3316 5.61817 10.0622 4.04441L5.5641 1.89206C5.47952 1.85149 5.38772 1.82843 5.29414 1.82427C5.20056 1.8201 5.1071 1.83491 5.0193 1.86781C4.9315 1.9007 4.85114 1.95103 4.78298 2.0158C4.71482 2.08057 4.66025 2.15847 4.6225 2.24488L2.45561 7.1848C2.38206 7.35437 2.37753 7.54628 2.443 7.71918C2.50847 7.89208 2.6387 8.03214 2.80563 8.10919C3.75774 8.56092 5.65154 9.46064 7.35624 10.2899C13.503 13.289 18.7152 13.0879 22.7023 6.55671'
      fill='url(#paint1_linear_1906_433)'
    />
    <defs>
      <linearGradient
        id='paint0_linear_1906_433'
        x1='2092.26'
        y1='1211.29'
        x2='1575.8'
        y2='35.8691'
        gradientUnits='userSpaceOnUse'
      >
        <stop offset='0.18' stopColor='#0052CC' />
        <stop offset='1' stopColor='#2684FF' />
      </linearGradient>
      <linearGradient
        id='paint1_linear_1906_433'
        x1='21.9384'
        y1='-132.537'
        x2='539.216'
        y2='1043.44'
        gradientUnits='userSpaceOnUse'
      >
        <stop offset='0.18' stopColor='#0052CC' />
        <stop offset='1' stopColor='#2684FF' />
      </linearGradient>
    </defs>
  </svg>
)
const SlackIconWrapper = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M5.63802 14.8966C5.63802 16.1743 4.60543 17.2069 3.32777 17.2069C2.05011 17.2069 1.01752 16.1743 1.01752 14.8966C1.01752 13.619 2.05011 12.5864 3.32786 12.5864H5.63794L5.63802 14.8966ZM6.79319 14.8966C6.79319 13.619 7.82578 12.5864 9.10345 12.5864C10.3811 12.5864 11.4137 13.619 11.4137 14.8967V20.6723C11.4137 21.9499 10.3811 22.9826 9.10336 22.9826C7.82587 22.9826 6.79319 21.9499 6.79319 20.6723V14.8966Z'
      fill='#E01E5A'
    />
    <path
      d='M9.10342 5.62051C7.82575 5.62051 6.79308 4.58792 6.79308 3.31025C6.79308 2.03259 7.82584 1 9.10342 1C10.381 1 11.4137 2.03259 11.4137 3.31025V5.62059L9.10342 5.62051ZM9.10342 6.79316C10.3811 6.79316 11.4137 7.82575 11.4137 9.10341C11.4137 10.3811 10.3811 11.4137 9.10333 11.4137H3.31034C2.03259 11.4137 1 10.3811 1 9.10333C1 7.82584 2.03259 6.79316 3.31025 6.79316H9.10342Z'
      fill='#36C5F0'
    />
    <path
      d='M18.362 9.10341C18.362 7.82575 19.3946 6.79308 20.6721 6.79308C21.9497 6.79308 22.9825 7.82575 22.9825 9.10341C22.9825 10.3811 21.9498 11.4137 20.6721 11.4137H18.362V9.10341ZM17.2068 9.10341C17.2068 10.3811 16.1741 11.4137 14.8965 11.4137C13.6189 11.4137 12.5863 10.3811 12.5863 9.10333V3.31034C12.5863 2.03259 13.6189 1 14.8965 1C16.174 1 17.2067 2.03259 17.2067 3.31025L17.2068 9.10341Z'
      fill='#2EB67D'
    />
    <path
      d='M14.8965 18.3621C16.1741 18.3621 17.2068 19.3947 17.2068 20.6723C17.2068 21.9498 16.1741 22.9826 14.8965 22.9826C13.6189 22.9826 12.5863 21.9499 12.5863 20.6723V18.3621H14.8965ZM14.8965 17.2069C13.6189 17.2069 12.5863 16.1743 12.5863 14.8966C12.5863 13.619 13.6189 12.5864 14.8966 12.5864H20.6896C21.9673 12.5864 23 13.619 23 14.8967C23 16.1743 21.9673 17.2069 20.6896 17.2069H14.8965Z'
      fill='#ECB22E'
    />
  </svg>
)
const MicrosoftTeamsIconWrapper = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M16.3453 9.59766H22.0279C22.5648 9.59766 23 10.0286 23 10.5601V15.6844C23 17.6378 21.4005 19.2214 19.4273 19.2214H19.4104C17.4373 19.2217 15.8375 17.6384 15.8372 15.6849V10.1007C15.8372 10.0347 15.8503 9.96923 15.8758 9.90819C15.9013 9.84714 15.9388 9.79168 15.9859 9.74496C16.0331 9.69824 16.0892 9.66118 16.1508 9.63591C16.2125 9.61063 16.2785 9.59763 16.3453 9.59766Z'
      fill='#5059C9'
    />
    <path
      d='M20.186 8.58478C21.4576 8.58478 22.4884 7.56429 22.4884 6.30545C22.4884 5.04661 21.4576 4.02612 20.186 4.02612C18.9145 4.02612 17.8837 5.04661 17.8837 6.30545C17.8837 7.56429 18.9145 8.58478 20.186 8.58478Z'
      fill='#5059C9'
    />
    <path
      d='M13.0232 8.5847C14.8599 8.5847 16.3488 7.11067 16.3488 5.29235C16.3488 3.47404 14.8599 2 13.0232 2C11.1866 2 9.69763 3.47404 9.69763 5.29235C9.69763 7.11067 11.1866 8.5847 13.0232 8.5847Z'
      fill='#7B83EB'
    />
    <path
      d='M17.4575 9.59766H8.07737C7.54688 9.61067 7.12724 10.0465 7.13953 10.5717V16.4163C7.06545 19.568 9.58402 22.1833 12.7674 22.2605C15.9508 22.1833 18.4694 19.568 18.3954 16.4163V10.5717C18.4077 10.0465 17.988 9.61067 17.4575 9.59766Z'
      fill='#7B83EB'
    />
    <path
      opacity='0.1'
      d='M13.2791 9.59766V17.788C13.2778 17.9717 13.2218 18.1509 13.118 18.3032C13.0143 18.4555 12.8674 18.5741 12.6958 18.644C12.5841 18.6908 12.464 18.7149 12.3428 18.7149H7.58965C7.52313 18.5477 7.46178 18.3805 7.41056 18.2084C7.23146 17.6272 7.14009 17.023 7.13942 16.4153V10.5702C7.12713 10.0458 7.54608 9.61067 8.07571 9.59774L13.2791 9.59766Z'
      fill='black'
    />
    <path
      opacity='0.2'
      d='M12.7674 9.59766V18.2945C12.7674 18.4146 12.7431 18.5334 12.6958 18.644C12.6252 18.8139 12.5054 18.9593 12.3516 19.062C12.1977 19.1648 12.0167 19.2202 11.8311 19.2214H7.83029C7.74332 19.0543 7.66142 18.8871 7.58983 18.7149C7.52106 18.5493 7.46127 18.3803 7.41074 18.2084C7.23164 17.6272 7.14027 17.023 7.13961 16.4153V10.5702C7.12732 10.0458 7.54626 9.61067 8.0759 9.59774L12.7674 9.59766Z'
      fill='black'
    />
    <path
      opacity='0.2'
      d='M12.7674 9.59766V17.2815C12.7636 17.7918 12.3467 18.2046 11.8311 18.2084H7.41068C7.23158 17.6272 7.14021 17.023 7.13955 16.4153V10.5702C7.12726 10.0458 7.5462 9.61067 8.07583 9.59774L12.7674 9.59766Z'
      fill='black'
    />
    <path
      opacity='0.2'
      d='M12.2558 9.59766V17.2815C12.2519 17.7918 11.835 18.2046 11.3195 18.2084H7.41068C7.23158 17.6272 7.14021 17.023 7.13955 16.4153V10.5702C7.12726 10.0458 7.5462 9.61067 8.07583 9.59774L12.2558 9.59766Z'
      fill='black'
    />
    <path
      opacity='0.1'
      d='M13.2791 6.97905V8.57452C13.1921 8.57962 13.1102 8.58473 13.0232 8.58473C12.9362 8.58473 12.8543 8.57962 12.7674 8.57452C12.5947 8.56317 12.4234 8.53606 12.2558 8.49353C11.745 8.37377 11.2702 8.13602 10.87 7.79974C10.4698 7.46345 10.1558 7.03817 9.95344 6.55868C9.88281 6.39534 9.82799 6.22572 9.78973 6.05212H12.3428C12.859 6.05408 13.2771 6.4679 13.2791 6.97905Z'
      fill='black'
    />
    <path
      opacity='0.2'
      d='M12.7674 7.48556V8.57456C12.5947 8.56321 12.4235 8.5361 12.2558 8.49356C11.7451 8.37379 11.2702 8.13604 10.8701 7.79976C10.4699 7.46347 10.1559 7.0382 9.95355 6.55872H11.8311C12.3474 6.56059 12.7654 6.97449 12.7674 7.48556Z'
      fill='black'
    />
    <path
      opacity='0.2'
      d='M12.7674 7.48556V8.57456C12.5947 8.56321 12.4235 8.5361 12.2558 8.49356C11.7451 8.37379 11.2702 8.13604 10.8701 7.79976C10.4699 7.46347 10.1559 7.0382 9.95355 6.55872H11.8311C12.3474 6.56059 12.7654 6.97449 12.7674 7.48556Z'
      fill='black'
    />
    <path
      opacity='0.2'
      d='M12.2558 7.48552V8.49352C11.7451 8.37376 11.2702 8.136 10.87 7.7997C10.4699 7.4634 10.1558 7.0381 9.95349 6.55859H11.3196C11.8359 6.56055 12.2539 6.97446 12.2558 7.48552Z'
      fill='black'
    />
    <path
      d='M1.93784 6.55856H11.3179C11.8359 6.55856 12.2558 6.97425 12.2558 7.48701V16.7734C12.2558 17.2861 11.8359 17.7018 11.318 17.7018H1.93784C1.41989 17.7018 1 17.2861 1 16.7734V7.4871C1 6.97416 1.41989 6.55856 1.93784 6.55856Z'
      fill='url(#paint0_linear_1906_459)'
    />
    <path
      d='M9.09595 10.093H7.2208V15.148H6.02618V10.093H4.15979V9.11243H9.09595V10.093Z'
      fill='white'
    />
    <defs>
      <linearGradient
        id='paint0_linear_1906_459'
        x1='196.536'
        y1='-65.9848'
        x2='920.038'
        y2='1199.73'
        gradientUnits='userSpaceOnUse'
      >
        <stop stopColor='#5A62C3' />
        <stop offset='0.5' stopColor='#4D55BD' />
        <stop offset='1' stopColor='#3940AB' />
      </linearGradient>
    </defs>
  </svg>
)

const VantaIconWrapper = () => (
<svg xmlns="http://www.w3.org/2000/svg" width="36" height="9" viewBox="0 0 36 9" fill="none">
  <path fillRule="evenodd" clipRule="evenodd" d="M11.3098 1.9292C13.3367 1.92925 14.38 2.81411 14.3801 4.46045V6.76904C14.3801 7.18869 14.5824 7.47615 14.8943 7.49414L15.0349 7.48535C15.1641 7.46017 15.2782 7.55505 15.2415 7.70654C15.0993 8.23887 14.6497 8.68652 13.7913 8.68652C13.1276 8.68639 12.612 8.39202 12.3792 7.92481C12.0108 8.47834 11.2234 8.80957 10.2771 8.80957C8.91406 8.80939 8.05355 8.13385 8.05347 7.05322C8.05347 6.09472 8.76682 5.32563 10.1189 5.03027C10.6713 4.89579 11.4564 4.82523 12.1565 4.8252V4.38721C12.1565 3.2859 11.657 2.57812 10.7239 2.57812C10.1371 2.57827 9.73926 2.88497 9.64722 2.96338C9.77664 3.01147 10.2037 3.26781 10.2053 3.77344C10.2053 4.26652 9.77587 4.66699 9.24585 4.66699C8.89778 4.66691 8.59373 4.49301 8.42554 4.23486C8.32096 4.05618 8.25041 3.87036 8.25122 3.68115C8.25491 2.62737 9.49466 1.9292 11.3098 1.9292ZM12.1565 5.51367C11.7144 5.52588 11.2356 5.59935 10.929 5.73486C10.4737 5.93135 10.2654 6.34047 10.2654 6.80713C10.2655 7.40921 10.646 7.79004 11.2117 7.79004C11.7277 7.78993 12.0834 7.48203 12.1565 6.9668V5.51367Z" fill="var(--foreground)"/>
  <path fillRule="evenodd" clipRule="evenodd" d="M31.8469 1.9292C33.8741 1.9292 34.9171 2.81406 34.9172 4.46045V6.76904C34.9172 7.18858 35.1196 7.47601 35.4314 7.49414L35.572 7.48535C35.7013 7.46017 35.8169 7.55505 35.78 7.70654C35.6381 8.23886 35.1879 8.68629 34.3298 8.68652C33.6658 8.68652 33.1491 8.39218 32.9163 7.92481C32.548 8.47819 31.7618 8.80941 30.8157 8.80957C29.4525 8.80957 28.5921 8.134 28.592 7.05322C28.592 6.09475 29.3039 5.32565 30.656 5.03027C31.2085 4.89572 31.9948 4.8252 32.6951 4.8252V4.38721C32.6951 3.2859 32.194 2.57812 31.261 2.57812C30.6754 2.57837 30.2787 2.88444 30.1858 2.96338C30.316 3.01204 30.7423 3.26859 30.7439 3.77344C30.7439 4.26652 30.313 4.66699 29.783 4.66699C29.4352 4.66683 29.1308 4.49295 28.9627 4.23486C28.8581 4.05622 28.7874 3.87031 28.7883 3.68115C28.7919 2.62738 30.0318 1.92922 31.8469 1.9292ZM32.6936 5.51367C32.2516 5.5259 31.7727 5.59933 31.4661 5.73486C31.0108 5.93136 30.8025 6.34048 30.8025 6.80713C30.8026 7.40921 31.1833 7.79004 31.7488 7.79004C32.2649 7.79002 32.6207 7.48209 32.6936 6.9668V5.51367Z" fill="var(--foreground)"/>
  <path d="M26.3787 0.515625C26.5505 0.515625 26.6907 0.654452 26.6907 0.826172V1.72559C26.6907 1.82548 26.6089 1.90743 26.509 1.90723H26.3523C26.3095 1.9077 26.2747 1.9421 26.2747 1.98486V2.10059C26.2749 2.14354 26.3106 2.17822 26.3538 2.17822H27.8948C28.0337 2.1784 28.1464 2.29128 28.1467 2.43018V2.84326C28.1466 2.98255 28.0338 3.09503 27.8948 3.09522H26.6907V6.49805C26.6907 7.35419 27.0081 7.77082 27.6692 7.771C27.7397 7.76937 27.8203 7.75795 27.9021 7.7417C28.1172 7.69959 28.2628 7.95016 28.1233 8.11817C27.7647 8.54821 27.1724 8.78752 26.5193 8.7876C25.2102 8.7876 24.4876 8.00373 24.4876 6.58447V3.09522H23.7537C23.615 3.09522 23.5019 2.98266 23.5017 2.84326V2.43018C23.5021 2.29096 23.6151 2.17822 23.7537 2.17822H23.991C24.792 2.17822 25.0388 1.1111 25.1409 0.773438C25.1889 0.619619 25.3314 0.515905 25.4924 0.515625H26.3787Z" fill="var(--foreground)"/>
  <path d="M3.15064 0C3.40722 0.000467996 3.58196 0.26196 3.48462 0.499512L3.30298 0.94043C3.18356 1.2325 3.17961 1.5593 3.29126 1.85449L4.98462 6.32959C5.02216 6.42817 5.02964 6.53614 5.00513 6.63867C4.98448 6.72412 4.94803 6.81867 4.88941 6.89648C4.85915 6.93053 4.85781 6.96665 4.89087 6.9917L4.99634 7.07666C5.02575 7.09827 5.06304 7.09085 5.0857 7.06934C5.12821 7.02848 5.22217 6.8631 5.31421 6.62109L7.31372 1.59229C7.42298 1.31673 7.39022 1.00498 7.2273 0.757324L7.03687 0.467285C6.90502 0.266882 7.04902 0 7.28882 0L8.4021 0.00146484C8.65722 0.00146484 8.83267 0.257912 8.73902 0.495117L5.5896 8.43457C5.52611 8.59519 5.37124 8.70097 5.19849 8.70117H3.8523C3.67925 8.70117 3.52338 8.59558 3.45972 8.43457L1.02954 2.08154C0.85681 1.64392 0.60418 1.24231 0.285404 0.896484L0.112553 0.70752C-0.136265 0.437452 0.0543151 0.00032254 0.421635 0H3.15064Z" fill="var(--foreground)"/>
  <path d="M20.7932 1.96436C22.2086 1.96436 23.0095 2.937 23.0095 4.63477V7.02686C23.0095 7.35812 23.1241 7.67936 23.3333 7.93652L23.471 8.10645C23.6625 8.34255 23.4939 8.69531 23.1897 8.69531H20.7566C20.4955 8.69531 20.3201 8.42746 20.4255 8.18848L20.6292 7.72705C20.7376 7.48112 20.7932 7.21511 20.7932 6.94629V4.72119C20.7932 3.89747 20.4491 3.44092 19.8088 3.44092C19.3334 3.44113 18.9528 3.6976 18.6619 4.19971C18.4222 4.61353 18.309 5.0882 18.3088 5.56641V6.9375C18.3089 7.27073 18.424 7.59371 18.6355 7.85156L18.8435 8.10498C19.0369 8.34086 18.8689 8.69526 18.5637 8.69531H16.0002C15.7377 8.69531 15.5625 8.42486 15.6707 8.18555L15.908 7.66113C16.0219 7.41023 16.0808 7.13826 16.0808 6.86279V4.37988C16.0808 3.91757 15.9149 3.46932 15.6135 3.11865L15.3147 2.77148C15.1136 2.53736 15.2798 2.17422 15.5886 2.17383H17.6409C17.8736 2.17383 18.0626 2.36286 18.0627 2.5957V2.95752C18.0627 3.13712 18.0139 3.31368 17.9236 3.46875L17.7815 3.71777C17.7562 3.76185 17.7721 3.81827 17.8167 3.84229L17.9221 3.89941C17.9649 3.92186 18.018 3.90638 18.0422 3.86426C18.5437 2.99112 18.9091 1.96447 20.7932 1.96436Z" fill="var(--foreground)"/>
</svg>
)

const DrataIconWrapper = () => (
<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M14.2165 3.34265C15.6629 3.39274 17.1129 3.63735 18.4646 4.16495C19.6282 4.61661 20.7222 5.27527 21.6133 6.15489C22.5719 7.08214 23.2815 8.26328 23.6537 9.54402C23.8768 10.2922 23.9768 11.0724 24 11.8516V12.0109C23.9924 13.28 23.7382 14.5584 23.1728 15.6999C22.635 16.8062 21.8217 17.7724 20.8444 18.5171C19.8139 19.3063 18.6142 19.8582 17.3652 20.1993C16.3409 20.482 15.2801 20.6191 14.2195 20.6574H6.4913C8.02786 19.2557 9.5656 17.8543 11.1015 16.4519C11.4517 16.4532 11.8022 16.4454 12.1524 16.4598C12.9139 16.4533 13.6767 16.4791 14.4372 16.4242C15.1486 16.3702 15.8586 16.2478 16.534 16.0136C17.0392 15.8362 17.5269 15.5956 17.952 15.2674C18.3975 14.9235 18.7736 14.4841 19.0227 13.9781C19.3506 13.3206 19.4681 12.5742 19.4482 11.8447C19.4295 11.2105 19.3003 10.5716 19.0098 10.0042C18.7471 9.47897 18.3483 9.02577 17.8738 8.68115C17.3453 8.29337 16.7329 8.03364 16.1036 7.85984C15.3188 7.64451 14.503 7.56093 13.6911 7.54814C12.8318 7.54945 11.9717 7.54684 11.1124 7.54913C9.57341 6.14337 8.0479 4.72125 6.48437 3.34265H14.2165Z" fill="var(--foreground)"/>
<path d="M6.49229 7.43138C7.04311 8.25646 7.70082 9.01305 8.4565 9.65683C9.19718 10.2924 10.0318 10.8148 10.9175 11.2223C11.5819 11.5365 12.29 11.7414 12.9777 11.9951C12.5761 12.1388 12.171 12.2739 11.7685 12.4146C10.8327 12.7547 9.94247 13.2234 9.14027 13.8138C8.10927 14.5692 7.22867 15.5166 6.48635 16.5538C5.6092 15.2237 4.43425 14.0873 3.05863 13.2805C2.47581 12.9349 1.85855 12.6483 1.22306 12.4137C0.815902 12.2713 0.407154 12.1318 0 11.9891C0.284754 11.8802 0.576813 11.7888 0.863858 11.6863C2.11562 11.2749 3.30221 10.652 4.32621 9.8201C5.16503 9.14099 5.89106 8.32681 6.49229 7.43138Z" fill="var(--foreground)"/>
</svg>

)
const AWSIconWrapper=()=>(
  <svg className='dark:[&>path:first-child]:fill-white' width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M6.763 10.036C6.763 10.358 6.795 10.618 6.852 10.806C6.917 10.994 6.998 11.198 7.111 11.418C7.159 11.498 7.175 11.578 7.175 11.65C7.175 11.754 7.111 11.858 6.974 11.962L6.499 12.277C6.402 12.341 6.306 12.373 6.218 12.373C6.106 12.373 5.994 12.317 5.882 12.213C5.769 12.101 5.673 11.973 5.593 11.829C5.513 11.677 5.433 11.509 5.345 11.309C4.757 12.013 4.008 12.365 3.098 12.365C2.444 12.365 1.926 12.181 1.554 11.813C1.182 11.445 0.992 10.958 0.992 10.35C0.992 9.702 1.214 9.175 1.666 8.775C2.118 8.375 2.716 8.175 3.468 8.175C3.728 8.175 3.996 8.199 4.28 8.239C4.564 8.279 4.856 8.343 5.164 8.415V7.903C5.164 7.327 5.044 6.919 4.812 6.671C4.572 6.423 4.168 6.303 3.588 6.303C3.316 6.303 3.036 6.335 2.748 6.407C2.46 6.479 2.18 6.567 1.908 6.679C1.796 6.727 1.708 6.759 1.652 6.775C1.596 6.791 1.556 6.799 1.524 6.799C1.396 6.799 1.332 6.711 1.332 6.527V6.167C1.332 6.023 1.356 5.919 1.412 5.855C1.468 5.791 1.564 5.727 1.708 5.663C1.98 5.527 2.3 5.415 2.668 5.327C3.036 5.231 3.428 5.187 3.844 5.187C4.732 5.187 5.385 5.391 5.813 5.799C6.233 6.207 6.449 6.831 6.449 7.671V10.036H6.763ZM3.428 11.309C3.68 11.309 3.94 11.261 4.212 11.165C4.484 11.069 4.724 10.894 4.924 10.654C5.044 10.51 5.132 10.35 5.172 10.166C5.212 9.982 5.244 9.766 5.244 9.518V9.23C5.02 9.174 4.78 9.126 4.532 9.094C4.284 9.062 4.044 9.046 3.812 9.046C3.316 9.046 2.948 9.142 2.692 9.342C2.436 9.542 2.312 9.822 2.312 10.19C2.312 10.534 2.404 10.79 2.596 10.966C2.78 11.197 3.06 11.309 3.428 11.309ZM9.429 12.277C9.269 12.277 9.165 12.253 9.101 12.197C9.037 12.149 8.981 12.037 8.933 11.877L7.243 5.919C7.195 5.751 7.171 5.639 7.171 5.575C7.171 5.439 7.243 5.367 7.387 5.367H8.083C8.251 5.367 8.363 5.391 8.419 5.447C8.483 5.495 8.531 5.607 8.579 5.767L9.789 10.654L10.903 5.767C10.943 5.599 10.991 5.495 11.055 5.447C11.119 5.399 11.239 5.367 11.399 5.367H11.967C12.135 5.367 12.247 5.391 12.311 5.447C12.375 5.495 12.431 5.607 12.463 5.767L13.593 10.718L14.835 5.767C14.883 5.599 14.939 5.495 14.995 5.447C15.059 5.399 15.171 5.367 15.331 5.367H15.995C16.139 5.367 16.219 5.431 16.219 5.575C16.219 5.615 16.211 5.655 16.203 5.703C16.195 5.751 16.179 5.815 16.147 5.903L14.409 11.861C14.361 12.029 14.305 12.133 14.241 12.181C14.177 12.229 14.065 12.261 13.913 12.261H13.305C13.137 12.261 13.025 12.237 12.961 12.181C12.897 12.125 12.841 12.021 12.809 11.853L11.703 7.095L10.605 11.845C10.565 12.013 10.509 12.117 10.445 12.173C10.381 12.229 10.261 12.261 10.101 12.261H9.429V12.277ZM19.268 12.405C18.844 12.405 18.42 12.357 18.012 12.261C17.604 12.165 17.284 12.061 17.068 11.941C16.932 11.861 16.844 11.773 16.812 11.693C16.78 11.613 16.764 11.525 16.764 11.445V11.069C16.764 10.885 16.836 10.797 16.972 10.797C17.02 10.797 17.068 10.805 17.116 10.821C17.164 10.837 17.236 10.869 17.316 10.901C17.58 11.021 17.868 11.117 18.172 11.181C18.484 11.245 18.788 11.277 19.1 11.277C19.596 11.277 19.98 11.189 20.244 11.013C20.508 10.837 20.644 10.582 20.644 10.254C20.644 10.03 20.572 9.846 20.428 9.694C20.284 9.542 20.012 9.406 19.62 9.278L18.5 8.934C17.908 8.75 17.468 8.478 17.196 8.118C16.924 7.766 16.784 7.367 16.784 6.935C16.784 6.583 16.86 6.271 17.012 5.999C17.164 5.727 17.372 5.495 17.628 5.303C17.884 5.103 18.18 4.959 18.524 4.855C18.868 4.751 19.228 4.703 19.612 4.703C19.804 4.703 20.004 4.711 20.196 4.735C20.396 4.759 20.58 4.791 20.764 4.831C20.94 4.879 21.108 4.927 21.268 4.983C21.428 5.039 21.548 5.095 21.636 5.151C21.756 5.223 21.844 5.295 21.892 5.375C21.94 5.447 21.964 5.551 21.964 5.687V6.031C21.964 6.215 21.892 6.311 21.756 6.311C21.692 6.311 21.588 6.279 21.452 6.215C21.012 6.015 20.524 5.919 19.988 5.919C19.54 5.919 19.188 5.991 18.948 6.143C18.708 6.295 18.584 6.527 18.584 6.847C18.584 7.071 18.664 7.263 18.824 7.415C18.984 7.567 19.276 7.719 19.692 7.855L20.788 8.199C21.372 8.383 21.796 8.639 22.052 8.967C22.308 9.295 22.432 9.678 22.432 10.11C22.432 10.47 22.356 10.798 22.212 11.085C22.06 11.373 21.852 11.621 21.588 11.821C21.324 12.029 21.004 12.181 20.636 12.285C20.252 12.365 19.812 12.405 19.268 12.405Z" fill="#252F3E"/>
  <path d="M20.516 16.413C18.38 17.971 15.316 18.791 12.684 18.791C8.988 18.791 5.66 17.459 3.148 15.211C2.948 15.027 3.132 14.779 3.372 14.923C6.068 16.481 9.38 17.427 12.804 17.427C15.116 17.427 17.652 16.955 19.98 15.979C20.324 15.827 20.62 16.197 20.516 16.413Z" fill="#FF9900"/>
  <path d="M21.292 15.523C21.028 15.179 19.524 15.363 18.844 15.443C18.636 15.467 18.604 15.283 18.788 15.155C19.988 14.323 21.924 14.563 22.148 14.843C22.372 15.131 22.084 17.075 20.956 18.011C20.788 18.155 20.628 18.075 20.7 17.891C20.924 17.323 21.556 15.859 21.292 15.523Z" fill="#FF9900"/>
</svg>
)
const BitbucketIconWrapper = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.27 4C3.12 4 2.99 4.04 2.88 4.13C2.77 4.22 2.71 4.36 2.72 4.5L4.92 19.57C4.96 19.83 5.18 20.02 5.44 20.02H18.79C18.99 20.02 19.17 19.88 19.2 19.68L21.4 4.51C21.41 4.37 21.35 4.23 21.24 4.14C21.13 4.05 21 4.01 20.85 4.01H3.27V4ZM14.51 14.67H9.59L8.35 9.34H15.64L14.51 14.67Z" fill="#2684FF"/>
    <path d="M20.74 9.34H15.64L14.51 14.67H9.59L5.05 19.89C5.15 19.97 5.29 20.02 5.44 20.02H18.79C18.99 20.02 19.17 19.88 19.2 19.68L20.74 9.34Z" fill="url(#bitbucket_gradient)"/>
    <defs>
      <linearGradient id="bitbucket_gradient" x1="21.84" y1="10.72" x2="10.82" y2="17.71" gradientUnits="userSpaceOnUse">
        <stop offset="0.18" stopColor="#0052CC"/>
        <stop offset="1" stopColor="#2684FF"/>
      </linearGradient>
    </defs>
  </svg>
)

// Gradient ids are namespaced (`supabase__…`) because inline SVGs share the
// page's id space — an unprefixed `paint0_linear` would collide with any other
// inline icon using Figma's default export names and silently repaint one of
// them. Same reason as `bitbucket_gradient` above.
const SupabaseIconWrapper = () => (
  <svg width="24" height="24" viewBox="0 0 109 113" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M63.7076 110.284C60.8481 113.885 55.0502 111.912 54.9813 107.314L53.9738 40.0627L99.1935 40.0627C107.384 40.0627 111.952 49.5228 106.859 55.9374L63.7076 110.284Z" fill="url(#supabase__paint0_linear)"/>
    <path d="M63.7076 110.284C60.8481 113.885 55.0502 111.912 54.9813 107.314L53.9738 40.0627L99.1935 40.0627C107.384 40.0627 111.952 49.5228 106.859 55.9374L63.7076 110.284Z" fill="url(#supabase__paint1_linear)" fillOpacity="0.2"/>
    <path d="M45.317 2.07103C48.1765 -1.53037 53.9745 0.442937 54.0434 5.041L54.4849 72.2922H9.83113C1.64038 72.2922 -2.92775 62.8321 2.1655 56.4175L45.317 2.07103Z" fill="#3ECF8E"/>
    <defs>
      <linearGradient id="supabase__paint0_linear" x1="53.9738" y1="54.974" x2="94.1635" y2="71.8295" gradientUnits="userSpaceOnUse">
        <stop stopColor="#249361"/>
        <stop offset="1" stopColor="#3ECF8E"/>
      </linearGradient>
      <linearGradient id="supabase__paint1_linear" x1="36.1558" y1="30.578" x2="54.4844" y2="65.0806" gradientUnits="userSpaceOnUse">
        <stop/>
        <stop offset="1" stopOpacity="0"/>
      </linearGradient>
    </defs>
  </svg>
)

// MDM provider icons
const IntuneIconWrapper = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 3H11V11H3V3Z" fill="#F25022"/>
    <path d="M13 3H21V11H13V3Z" fill="#7FBA00"/>
    <path d="M3 13H11V21H3V13Z" fill="#00A4EF"/>
    <path d="M13 13H21V21H13V13Z" fill="#FFB900"/>
  </svg>
)

const FleetDMIconWrapper = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="4" fill="#6A67CE"/>
    <path d="M7 8H17V10H7V8ZM7 11.5H14V13.5H7V11.5ZM7 15H11V17H7V15Z" fill="white"/>
  </svg>
)

const JamfIconWrapper = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="4" fill="#1A1A1A"/>
    <path d="M12 4C7.58 4 4 7.58 4 12C4 16.42 7.58 20 12 20C16.42 20 20 16.42 20 12C20 7.58 16.42 4 12 4ZM12 17.5C9.24 17.5 7 15.26 7 12.5C7 9.74 9.24 7.5 12 7.5C14.76 7.5 17 9.74 17 12.5C17 15.26 14.76 17.5 12 17.5Z" fill="white"/>
    <circle cx="12" cy="12.5" r="2.5" fill="white"/>
  </svg>
)

const OneLoginIconWrapper = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="4" fill="#1C1C1C"/>
    <circle cx="12" cy="12" r="6" fill="#00A1E0"/>
    <circle cx="12" cy="12" r="2.5" fill="#1C1C1C"/>
  </svg>
)

const JumpCloudIconWrapper = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 4C7.58 4 4 7.58 4 12C4 16.42 7.58 20 12 20C16.42 20 20 16.42 20 12C20 7.58 16.42 4 12 4Z" fill="#36B37E"/>
    <path d="M8 13L11 16L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const SnykIconWrapper = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L3 7V17L12 22L21 17V7L12 2Z" fill="#4C4A73"/>
    <path d="M12 6L7 9V15L12 18L17 15V9L12 6Z" fill="white"/>
    <circle cx="12" cy="12" r="2" fill="#4C4A73"/>
  </svg>
)

const M365IconWrapper = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="10" height="10" rx="1" fill="#F25022"/>
    <rect x="13" y="1" width="10" height="10" rx="1" fill="#7FBA00"/>
    <rect x="1" y="13" width="10" height="10" rx="1" fill="#00A4EF"/>
    <rect x="13" y="13" width="10" height="10" rx="1" fill="#FFB900"/>
  </svg>
)

const KubernetesIconWrapper = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 1L2.5 6.5V17.5L12 23L21.5 17.5V6.5L12 1Z" fill="#326CE5"/>
    <path d="M12 5.5L7.5 8.25V13.75L12 16.5L16.5 13.75V8.25L12 5.5Z" fill="white" stroke="#326CE5" strokeWidth="0.5"/>
    <circle cx="12" cy="11" r="1.5" fill="#326CE5"/>
  </svg>
)

const VendorLogoIcon = ({ slug }: { slug: string }) => {
  const logo = getVendorLogo(slug)
  return logo ? (
    <img src={logo.src} alt='' width={24} height={24} className={logo.className} />
  ) : (
    <div className='h-8 w-8 rounded-full bg-gray-200' />
  )
}

// Keep inline artwork and non-brand symbols; file-backed logos use the registry.
const INLINE_INTEGRATION_ICONS: Partial<Record<IntegrationType, ComponentType>> = {
  GitHub: GitHubIconWrapper,
  GITHUB_SECURITY_ALERTS: GitHubIconWrapper,
  GitLab: GitLabIconWrapper,
  Bitbucket: BitbucketIconWrapper,
  Jira: JiraIconWrapper,
  Linear: LinearIconWrapper,
  Intercom: IntercomIconWrapper,
  Docusaurus: DocusaurusIconWrapper,
  VitePress: VitePressIconWrapper,
  GitBook: GitBookIconWrapper,
  LocalFile: LocalFileIconWrapper,
  GoogleDrive: GoogleDriveIconWrapper,
  Notion: NotionIconWrapper,
  Confluence: ConfluenceIconWrapper,
  Slack: SlackIconWrapper,
  MicrosoftTeams: MicrosoftTeamsIconWrapper,
  Vanta: VantaIconWrapper,
  Drata: DrataIconWrapper,
  AWS_READONLY: AWSIconWrapper,
  AWS_SECURITY_HUB: AWSIconWrapper,
  Supabase: SupabaseIconWrapper,
  MicrosoftIntune: IntuneIconWrapper,
  FleetDM: FleetDMIconWrapper,
  JamfPro: JamfIconWrapper,
  OneLogin: OneLoginIconWrapper,
  Snyk: SnykIconWrapper,
  Orca: () => <div className='flex h-8 w-8 items-center justify-center rounded-md bg-[#0F2942] text-[10px] font-bold text-white'>Orca</div>,
  M365: M365IconWrapper,
  Kubernetes: KubernetesIconWrapper,
}

export const IntegrationIcon = ({ type }: { type: IntegrationType }) => {
  const InlineIcon = INLINE_INTEGRATION_ICONS[type]
  if (InlineIcon) return <InlineIcon />
  const entry = getPrimaryIntegrationByType(type)
  return <VendorLogoIcon slug={entry?.slug ?? type.toLowerCase()} />
}

/** Brand art for action-catalog provider keys, including legacy underscore keys. */
export const ProviderLogoIcon = ({ providerKey }: { providerKey: string }) => {
  if (!hasProviderLogo(providerKey)) return null
  const entry = getIntegrationBySlug(providerKey.replaceAll('_', '-')) ??
    INTEGRATION_REGISTRY.find((entry) => entry.adapterSlug === providerKey)
  return entry?.integrationType
    ? <IntegrationIcon type={entry.integrationType} />
    : <VendorLogoIcon slug={providerKey} />
}

export const hasProviderLogo = (providerKey: string): boolean =>
  getVendorLogo(providerKey) !== null
